import moment from 'moment';

import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  forwardRef,
  OnInit,
  Output,
  AfterViewInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';

import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { IconService } from '@nrwl/icon-services';
import { EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RxIconModule } from '@nrwl/rx-icon';
import { MatIconModule } from '@angular/material/icon';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY',
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'MM/DD/YYYY',
    monthYearA11yLabel: 'MM/DD/YYYY',
  },
};

@Component({
  selector: 'rx-datepicker-with-input',
  templateUrl: './rx-datepicker-with-input.component.html',
  styleUrls: ['./rx-datepicker-with-input.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RxIconModule,
    MatDatepickerModule,
    MatIconModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RxDatepickerWithInputComponent),
      multi: true,
    },
    // `MomentDateAdapter` and `MAT_MOMENT_DATE_FORMATS` can be automatically provided by importing
    // `MatMomentDateModule` in your applications root module. We provide it at the component level
    // here, due to limitations of our example generation script.
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class RxDatepickerWithInputComponent implements OnInit, ControlValueAccessor, AfterViewInit {
  public value = '';
  public dateRegex =
    /^02\/(?:[01]\d|2\d)\/(?:19|20)(?:0[048]|[13579][26]|[2468][048])|(?:0[13578]|10|12)\/(?:[0-2]\d|3[01])\/(?:19|20)\d{2}|(?:0[469]|11)\/(?:[0-2]\d|30)\/(?:19|20)\d{2}|02\/(?:[0-1]\d|2[0-8])\/(?:19|20)\d{2}$/;

  onChange: (value?: any) => void;

  onTouch: (event: any) => void;

  @Output() onFormChange: EventEmitter<any> = new EventEmitter();

  @Output() isInvalidDate: EventEmitter<boolean> = new EventEmitter();

  @Input() placeholder = '';

  @Input() autoFocus = false;

  @Input() hasError = false;
  @Input() format = '';

  @Input() isDisabled: boolean;

  @Input() inputType: boolean;

  @Input() edit: boolean;

  @Input() iconName: string | any = 'calendar';

  @Input() iconSize: number | any = 22;
  @Input() iconColor: string | any = '#91979d';

  @Input() fxFlex;

  @ViewChild('input') input: ElementRef;

  constructor(private iconService: IconService) {}

  ngOnInit(): void {
    if (this.iconName) {
      this.iconService.register(this.iconName);
    }
  }

  ngAfterViewInit() {
    if (this.autoFocus) {
      this.onFocus();
    }
  }

  writeValue(value: any) {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisableState(status: boolean) {
    this.isDisabled = status;
  }

  onInput({ target }) {
    const isDateValid = moment(target.value, 'MM/DD/YYYY', true).year() > 1900;
    if ((this.onChange && isDateValid) || !target.value) {
      this.onChange(target.value);
      this.onValueChange(target.value);
      this.isInvalidDate.emit(false);
    } else {
      this.isInvalidDate.emit(true);
      this.onValueChange(target.value);
    }
  }

  addEventChange(event: MatDatepickerInputEvent<Date>) {
    const isDateValid = moment(event.value, 'MM/DD/YYYY', true).year() > 1900;
    if (this.onChange && isDateValid) {
      this.onChange(moment(event.value, this.format));
      this.onValueChange(moment(event.value).format());
      this.isInvalidDate.emit(false);
    } else {
      this.isInvalidDate.emit(true);
      this.onValueChange(moment(event.value).format());
    }
  }

  onTouched(value) {
    if (this.onTouch) {
      this.onTouch(value);
    }
  }

  onFocus() {
    //Normal Focus Method
    this.input.nativeElement.focus();

    // Another Method for set Focus
    //  this.input.nativeElement.select();
  }

  onValueChange(value) {
    this.onFormChange.emit(value);
  }
}
